import { useGlobalContext } from '#contexts/GlobalContext';
import {
  useDeleteOrder,
  useGetDeviceStatisticsInterval,
  useGetOrdersInterval,
} from '@vindr/services/actions/order.action';
import { ParamsGetOrders, ResponseGetOrders } from '@vindr/services/apis/order.api';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { PaginationProps } from '@vindr/services/interfaces';
import keycloak from '@vindr/services/keycloak';
import { Assign, SidePanel, SiderLayout, Table } from '@vindr/ui';
import { onClickRow } from '@vindr/utils/helpers/common.helper';
import useWindowSize from '@vindr/utils/hooks/useWindowSize';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import CreateOrder from './CreateOrder';
import EditOrder from './EditOrder';
import Filter from './Filter';
import OrderListHeader from './OrderListHeader';
import ReceptionWidget from './ReceptionWidget';
import useContextMenu, { MENU_TYPE } from './useContextMenu';
import useDefinedColumns from './useDefinedColumns';

export const INITIAL_SORTER_ORDER: {
  sort_field: Parameters<typeof useGetOrdersInterval>[0]['sort_field'];
  sort_order: Parameters<typeof useGetOrdersInterval>[0]['sort_order'];
} = {
  sort_field: 'scheduled_date',
  sort_order: SORT_ORDER.DESC,
};

export const INITIAL_FILTER_ORDER = {
  scheduled_date_from: dayjs().startOf('days').toISOString(),
  scheduled_date_to: dayjs().endOf('days').toISOString(),
};

const Order = () => {
  const { width } = useWindowSize();
  const { message, modal } = useGlobalContext();
  const [editingOrder, setEditingOrder] = useState<ResponseGetOrders['data'][number]>(null);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const { mutate: deleteOrder } = useDeleteOrder();
  const [selectedRowKeys, setSelectedRowKeys] = useImmer<ResponseGetOrders['data']>([]);
  const [collapsedRight, setCollapsedRight] = useState(window.innerWidth < 640);
  const [collapsed, setCollapsed] = useState(false);

  const [assignState, setAssignState] = useState({ visible: false, order: null });
  /*   const handleContextMenuSelect = (type, record) => {
    switch (type) {
      case MENU_TYPE.ASSIGN:
        if ([ORDER_STATUS.NEW, ORDER_STATUS.ARRIVED, ORDER_STATUS.CANCEL].includes(record.status)) {
          message.error('Please match order first');
          return;
        }
        setAssignState({ visible: true, order: record });
        break;
      default:
        break;
    }
  };
  const contextMenuItems = useContextMenu({ handleContextMenuSelect }); */

  const handleRemove = (record) => {
    modal.confirm({
      className: 'delete-modal',
      title: t(MESSAGE.DIALOG.CFD_8000),
      okText: t('Delete'),
      onOk: () => {
        deleteOrder(
          { id: record?.id },
          {
            onSuccess: () => {
              message.success(t(MESSAGE.SUCCESS.SCD_8002));
            },
            onError: (error: any) => {
              const error_message =
                (error?.response?.data?.error_code &&
                  MESSAGE.ERROR[error?.response?.data?.error_code]) ||
                'Delete error';

              message.error(t(error_message));
            },
          }
        );
      },
    });
  };

  const handleEdit = (record: typeof orders[number]) => {
    setEditingOrder(record);
    setIsEditingOrder(true);
  };

  const handleAssign = (record) => {
    setAssignState({ visible: true, order: record });
  };

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    size: 25,
  });
  const performedColumns = useDefinedColumns({
    onEdit: handleEdit,
    onRemove: handleRemove,
    onAssign: handleAssign,
    pagination,
  });
  const [params, setParams] = useState<ParamsGetOrders>(INITIAL_FILTER_ORDER);
  const [sorter, setSorter] = useState(INITIAL_SORTER_ORDER);
  const { t } = useTranslation('Vindoc');
  // const institutions = useGetInstitutions({ offset: 0, limit: 100 })?.data?.data || [];
  const data = useGetOrdersInterval({
    offset: (pagination.page - 1) * pagination.size,
    limit: pagination.size,
    ...sorter,
    ...params,
  });
  const count = data?.data?.count || 0;
  const orders = data?.data?.data || [];
  const isLoading = data?.isLoading;
  const isFetching = data?.isFetching;
  const refetch = data?.refetch;

  const [isRefetch, setIsRefetch] = useState(false);
  useEffect(() => {
    if (!isFetching) {
      // After fetching, set isRefetch to false;
      setIsRefetch(false);
    }
  }, [isFetching]);

  const forceSearch = (params) => {
    handleFilter(params);
    // Set refetch to true only when force search
    setIsRefetch(true);
    refetch?.();
  };

  const handleFilter = (p, type) => {
    setPagination({ ...pagination, page: 1 });
    setParams(p);
    setSelectedRowKeys(() => []);

    //? on mobile, not close when choose select multiple
    if (['status', 'modalities'].includes(type)) return;

    setCollapsedRight(true);
  };

  const deviceStatistics =
    useGetDeviceStatisticsInterval({
      offset: (pagination.page - 1) * pagination.size,
      limit: pagination.size,
      ...sorter,
      scheduled_date_from: params.scheduled_date_from,
      scheduled_date_to: params.scheduled_date_to,
      modalities: params?.modalities,
      institution_ids: params?.institution_ids,
    })?.data?.data || [];

  const handleContextMenuSelect = (type: keyof typeof MENU_TYPE, record: typeof orders[number]) => {
    switch (type) {
      case MENU_TYPE.ASSIGN:
        handleAssign(record);
        break;
      case MENU_TYPE.EDIT:
        handleEdit(record);
        break;
      case MENU_TYPE.DELETE:
        handleRemove(record);
        break;
      default:
        break;
    }
  };
  const contextMenuItems = useContextMenu({ handleContextMenuSelect });

  const filter = (
    <Filter
      onChange={handleFilter}
      initFilters={INITIAL_FILTER_ORDER}
      className="min-w-[630px] flex-1"
      forceSearch={forceSearch}
    />
  );

  return (
    <SiderLayout
      rightSidebar={
        width < 640 ? (
          <SidePanel
            side="right"
            defaultCollapsed={true}
            width={215}
            collapsed={collapsedRight}
            onChange={(val) => setCollapsedRight(val)}
          >
            <div className="m-2">{filter}</div>
          </SidePanel>
        ) : null
      }
    >
      <div className="flex h-full flex-col overflow-auto rounded-sm bg-background-light-3 p-4 dark:bg-background-dark-3">
        <OrderListHeader
          totalItem={count}
          {...{ selectedRowKeys, setSelectedRowKeys }}
          collapsed={collapsed}
          onCollapse={() => setCollapsed((prev) => !prev)}
        />
        <ReceptionWidget collapsed={collapsed} devices={deviceStatistics} />
        <div className="flex flex-wrap justify-between">
          {width >= 640 && filter}
          {keycloak.hasOneOfPerm([PERMISSIONS.studylist.upload]) && (
            <CreateOrder {...{ selectedRowKeys, setSelectedRowKeys, params }} />
          )}
        </div>
        <div className="flex h-0 flex-1 flex-col">
          <Table
            // rowSelection={{ ...rowSelection }}
            wrapperClassName="min-h-[210px]"
            columns={performedColumns}
            dataSource={orders}
            selectedRowKeys={selectedRowKeys.map((item) => item.id)}
            setSelectedRowKeys={setSelectedRowKeys}
            // scroll={{ y: (height ?? 700) - 260 }}
            rowKey="id"
            pagination={pagination}
            loading={isLoading || isRefetch}
            count={count}
            contextMenu={{
              items: contextMenuItems,
              onClick: (menuItem, record: typeof orders[number]) => {
                if (menuItem.onClick) {
                  menuItem.onClick(record);
                }
              },
            }}
            onRow={(record) => {
              return {
                onClick: (event: MouseEvent) => onClickRow({ event, setSelectedRowKeys, record }),
              };
            }}
            rowSelection={{
              selectedRowKeys: selectedRowKeys.map((item) => item.id),
              onChange(selectedRowKeys, selectedRows: typeof orders, info) {
                // Can select if same Institution
                const isSameInstitution = selectedRows.every(
                  (item) => item.institution_id === selectedRows[0].institution_id
                );

                if (!isSameInstitution) {
                  message.error(t(MESSAGE.ERROR.EMSG_8015));

                  return;
                }

                setSelectedRowKeys(() => selectedRows);
              },
            }}
            onTableChange={(_, __, sorter) => {
              setSorter({
                sort_field: sorter.field,
                sort_order: sorter.order === 'ascend' ? 0 : 1,
              });
            }}
            onPaginationChange={(p) => p && setPagination(p)}
            // onRow={record => {
            //   return {
            //     onDoubleClick: event => {
            //       event.stopPropagation();
            //       event.preventDefault();
            //       if (event?._targetInst?.elementType === 'input') {
            //         // handle action doubleClick to checkbox
            //         return;
            //       }
            //       openViewer(record.study_instance_uid);
            //     },
            //   };
            // }}
            // onChange={handleOnChangeTable}
          />
        </div>
      </div>
      {assignState.order ? (
        <Assign
          visible={assignState.visible}
          order={assignState.order}
          onCancel={() => setAssignState({ visible: false, order: null })}
          message={message}
          modal={modal}
        />
      ) : null}
      <EditOrder
        orderData={editingOrder}
        isEditing={isEditingOrder}
        onFinish={() => {
          setEditingOrder(null);
          setIsEditingOrder(false);
        }}
      />
    </SiderLayout>
  );
};

export default Order;
